// HomePage — STRIKZA Command Center home.
//
// Rewritten to use the Football Products design kit (FpKit).
// All styling scoped under `.fp` via styles/football-products.css.
//
// Section order is archetype-routed (Ecosystem Edge §04): the
// resolved ArchetypeSurface decides ordering/visibility via the pure
// orderHomeSections() decision. HONEST DEFAULT: an unresolved profile
// yields DEFAULT_SURFACE → the exact canonical prototype order.

import "../../styles/football-products.css";

import { HomeActionQueue } from "./HomeActionQueue.tsx";
import { HomeActivitySnapshot } from "./HomeActivitySnapshot.tsx";
import { HomeGoalCard } from "./HomeGoalCard.tsx";
import { HomeHeroCard } from "./HomeHeroCard.tsx";
import { HomeSignalsPreview } from "./HomeSignalsPreview.tsx";
import { HomeStatStrip } from "./HomeStatStrip.tsx";
import { HomeRoiDashboard } from "./HomeRoiDashboard.tsx";
import { HomeCoverageStrip } from "./HomeCoverageStrip.tsx";
import { useArchetypeSurface } from "../../slips/useArchetypeSurface.ts";
import {
  orderHomeSections,
  type HomeSectionKey,
} from "./homeSectionOrder.ts";
import {
  FpHead,
  FpHero,
  FpCard,
  FpSkeleton,
  FpSeam,
  HeroChip,
} from "../football/_fpkit.tsx";

function renderSection(key: HomeSectionKey): JSX.Element | null {
  switch (key) {
    case "hero":
      return (
        <FpHero
          key="hero"
          kicker="COMMAND CENTER"
          title="Your Intelligence Hub"
          lede="AI-assembled picks across every active product. No manual scouting — the substrate does the work."
          chips={
            <>
              <HeroChip tone="accent">Live Products</HeroChip>
              <HeroChip>Substrate-Powered</HeroChip>
            </>
          }
          aside={<HomeHeroCard />}
        />
      );
    case "stats":
      return (
        <FpCard key="stats" kicker="PERFORMANCE" title="Stats Overview">
          <HomeStatStrip />
        </FpCard>
      );
    case "roi":
      return (
        <FpCard key="roi" kicker="RETURNS" title="ROI Dashboard">
          <HomeRoiDashboard />
        </FpCard>
      );
    case "grid":
      return (
        <div key="grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <FpCard kicker="QUEUE" title="Action Queue">
            <HomeActionQueue />
          </FpCard>
          <FpCard kicker="TARGET" title="Goal Tracker">
            <HomeGoalCard />
          </FpCard>
        </div>
      );
    case "signals":
      return (
        <FpCard key="signals" kicker="INTELLIGENCE" title="Signals Preview">
          <HomeSignalsPreview />
        </FpCard>
      );
    case "activity":
      return (
        <FpCard key="activity" kicker="AUDIT TRAIL" title="Recent Activity">
          <HomeActivitySnapshot />
        </FpCard>
      );
    default:
      return null;
  }
}

export function HomePage(): JSX.Element {
  const { surface, loading } = useArchetypeSurface();
  const order = orderHomeSections(surface);

  if (loading) {
    return (
      <div className="fp">
        <FpHead kicker="STRIKZA" title="Command Center" />
        <FpSkeleton rows={4} />
      </div>
    );
  }

  return (
    <div className="fp">
      <FpHead kicker="STRIKZA" title="Command Center" status="Active" live />
      {order.map((key, i) => (
        <div key={key}>
          {renderSection(key)}
          {i < order.length - 1 && <FpSeam />}
        </div>
      ))}
      <FpSeam />
      <HomeCoverageStrip />
    </div>
  );
}
